import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { keyframes } from "@emotion/react";
import { CONFIRM_WORD, confirmCopy, isConfirmMatch } from "./lspsSwitch";

const shake = keyframes`
  10%, 90% { transform: translateX(-1px); }
  20%, 80% { transform: translateX(2px); }
  30%, 50%, 70% { transform: translateX(-4px); }
  40%, 60% { transform: translateX(4px); }
`;

/**
 * Type-CONFIRM before Load Shifting / Peak Shaving switch (NEU-1225 design, NEU-1226).
 * Nothing changes until CONFIRM is typed exactly and the action pressed; Cancel, × and Esc
 * leave the switch as it was. The action button is aria-disabled rather than disabled so a
 * click on it still registers, and a wrong entry can be flagged.
 */
export default function LSPSConfirmDialog({ open, name, turnOn, busy, onCancel, onConfirm }) {
  const [text, setText] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const copy = confirmCopy(name, turnOn);
  const matched = isConfirmMatch(text);

  // Cleared every time it opens.
  useEffect(() => {
    if (open) {
      setText("");
      setInvalid(false);
    }
  }, [open]);

  const submit = () => {
    if (busy) return;
    if (!matched) {
      setInvalid(true);
      setShakeKey((k) => k + 1);
      return;
    }
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: "#161B22", color: "#fff", border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "14px", width: "100%", maxWidth: 480, p: "22px 24px", m: "16px",
        },
      }}
      aria-labelledby="lsps-confirm-title"
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "10px" }}>
        <Typography id="lsps-confirm-title" component="h3" sx={{ fontSize: "1.05rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
          <WarningAmberIcon sx={{ fontSize: "1rem", color: "#F59E0B" }} />
          {copy.title}
        </Typography>
        <IconButton aria-label="Close" size="small" onClick={onCancel} disabled={busy}
                    sx={{ color: "rgba(255,255,255,0.45)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.06)" } }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Typography sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.55, mb: "16px" }}>
        {copy.impact}
      </Typography>

      <Box component="label" htmlFor="lsps-confirm-input"
           sx={{ display: "block", fontSize: "0.82rem", fontWeight: 500, color: "rgba(255,255,255,0.65)", mb: "6px" }}>
        Type <Box component="span" sx={{ fontWeight: 700, color: "#fff" }}>{CONFIRM_WORD}</Box> to continue
      </Box>
      <TextField
        key={shakeKey}
        id="lsps-confirm-input"
        fullWidth
        size="small"
        autoFocus
        value={text}
        disabled={busy}
        error={invalid}
        onChange={(e) => {
          setText(e.target.value);
          setInvalid(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        inputProps={{ autoComplete: "off", autoCapitalize: "off", spellCheck: false, "aria-invalid": invalid }}
        sx={{
          animation: invalid ? `${shake} 0.32s cubic-bezier(0.36,0.07,0.19,0.97)` : "none",
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          "& .MuiInputBase-root": { color: "#fff", bgcolor: "rgba(255,255,255,0.03)" },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
          // Red while flagged, focused or hovered too: styles.css forces #ced4da !important on a
          // focused or hovered outline, so this matches its selector one level more specific.
          ...(invalid && {
            "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#EF4444 !important",
            },
          }),
        }}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px", mt: "22px" }}>
        <Button variant="text" onClick={onCancel} disabled={busy}
                sx={{ color: "rgba(255,255,255,0.55)", textTransform: "none", "&:hover": { color: "#fff" } }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          aria-disabled={!matched || busy}
          onClick={submit}
          sx={{
            bgcolor: "#EF4444", color: "#fff", textTransform: "none", fontWeight: 600, boxShadow: "none",
            opacity: matched && !busy ? 1 : 0.45, cursor: matched && !busy ? "pointer" : "not-allowed",
            "&:hover": { bgcolor: matched && !busy ? "#DC2626" : "#EF4444", boxShadow: "none" },
          }}
        >
          {busy ? "Saving…" : copy.action}
        </Button>
      </Box>
    </Dialog>
  );
}
